import { QuestionCircleOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { Form, Typography, Select, Alert, FormInstance, Tooltip } from 'antd';

import { IItemListItem, ModifyReason } from '~apis/tax-invoice';

import { Section } from './Section';
import { IssuanceUpdateFormData } from '../type';

export const TAX_INVOICE_REASON: Record<ModifyReason, string> = {
  CORRECTING_MISSTATEMENTS: '기재사항의 착오/정정',
  CHANGE_IN_SUPPLY_PRICE: '공급가액의 변동',
  RETURN_OF_GOODS: '환입(환불)',
  CONTRACT_CANCELLED: '계약의 해제',
  POST_OPENING_LOCAL_LC: '내국신용장 사후 개설',
  DOUBLE_ISSUANCE: '착오에 의한 이중발급',
};

const OPTIONS = [
  {
    label: ` ${TAX_INVOICE_REASON['CORRECTING_MISSTATEMENTS']}`,
    value: 'CORRECTING_MISSTATEMENTS',
    description: (
      <Flex direction="column" gap={4}>
        <Typography.Text>아래에 정정하려는 정확한 세금계산서 사항을 입력해주세요.</Typography.Text>
        <Typography.Text>
          당초발급 건에 대한 음(-)의 세금계산서는 클로브가 자동으로 발행해드려요.{' '}
        </Typography.Text>
      </Flex>
    ),
  },
  {
    label: `${TAX_INVOICE_REASON['CHANGE_IN_SUPPLY_PRICE']}`,
    value: 'CHANGE_IN_SUPPLY_PRICE',
    description: (
      <Flex direction="column" gap={4}>
        <Typography.Text>
          아래에 변동이 발생한 일자와 함께, 변동 후의 정확한 세금계산서 사항을 입력해주세요.
        </Typography.Text>
        <Typography.Text>
          당초발급 건 대비 증감분 계산은 클로브가 자동으로 입력해드려요.
        </Typography.Text>
      </Flex>
    ),
  },
  {
    label: `${TAX_INVOICE_REASON['RETURN_OF_GOODS']}`,
    value: 'RETURN_OF_GOODS',
    description: <Typography.Text>아래에 환입되는 공급가액을 입력해주세요.</Typography.Text>,
  },
  {
    label: `${TAX_INVOICE_REASON['CONTRACT_CANCELLED']}`,
    value: 'CONTRACT_CANCELLED',
    description: (
      <Flex direction="column" gap={4}>
        <Typography.Text>아래에 계약 해제된 일자를 입력해주세요.</Typography.Text>
        <Typography.Text>나머지 사항은 클로브가 자동으로 작성해드려요.</Typography.Text>
      </Flex>
    ),
  },
  {
    label: `${TAX_INVOICE_REASON['POST_OPENING_LOCAL_LC']}`,
    value: 'POST_OPENING_LOCAL_LC',
    description: (
      <Flex direction="column" gap={4}>
        <Typography.Text>아래에 내국신용장을 개설한 일자를 입력해주세요.</Typography.Text>
        <Typography.Text>
          클로브가 자동으로 음(-)의 세금계산서와 영세율 세금계산서를 작성해드려요.
        </Typography.Text>
      </Flex>
    ),
  },
  {
    label: `${TAX_INVOICE_REASON['DOUBLE_ISSUANCE']}`,
    value: 'DOUBLE_ISSUANCE',
    description: (
      <Typography.Text>클로브가 자동으로 음(-)의 세금계산서를 작성해드려요.</Typography.Text>
    ),
  },
];

interface Props {
  editType: ModifyReason;
  form: FormInstance<IssuanceUpdateFormData>;
}

const EditInfo: React.FC<Props> = ({ editType, form }) => {
  const values = Form.useWatch([], form);

  const minusPriceItemExist = values?.originItemList?.some(
    (item: IItemListItem) => item?.unitPrice < 0,
  );

  return (
    <Section
      direction="column"
      style={{
        padding: '36px 80px 24px 80px',
      }}
    >
      <Form.Item
        name="reason"
        style={{
          flex: 1,
          width: '100%',
        }}
        label={
          <Flex align="center" gap={5}>
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              수정 사유
            </Typography.Text>
            <Tooltip title="수정하는 이유를 선택해주세요. 어떤 수정 사유를 선택할지 고민되시나요? 여기를 클릭해 자세히 알아보세요.">
              <QuestionCircleOutlined
                width={14}
                height={14}
                style={{
                  color: colorTokens.iconDefault,
                }}
              />
            </Tooltip>
          </Flex>
        }
        required
      >
        <Select
          style={{
            width: 392,
          }}
          options={OPTIONS.filter((o) =>
            minusPriceItemExist
              ? o.value !== 'RETURN_OF_GOODS' && o.value !== 'CONTRACT_CANCELLED'
              : o,
          )}
          onChange={(value) => {
            form.resetFields(['itemList']);

            form.setFieldsValue({
              itemList:
                value === 'CONTRACT_CANCELLED' ||
                value === 'DOUBLE_ISSUANCE' ||
                value === 'RETURN_OF_GOODS'
                  ? form.getFieldValue('originItemList')?.map((item: any) => {
                      return {
                        ...item,
                        unitPrice: Math.abs(item.unitPrice),
                        flag: item.flag < 0 ? 1 : -1,
                      };
                    })
                  : form.getFieldValue('originItemList'),
            });
          }}
        />
      </Form.Item>
      <Alert
        type="info"
        banner
        showIcon={false}
        description={
          OPTIONS.find((o) => {
            return o.value === editType;
          })?.description
        }
      />
    </Section>
  );
};

export default EditInfo;
